import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck, FaChevronDown, FaChevronUp, FaChevronLeft} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { HelpCircle } from "lucide-react";


/**
 * Customer Financial Year Setup (ASPX -> React)
 * - Auto loads next financial year from MASTER pending row
 * - Shows listing from active Inventory DB
 * - Save -> confirm modal -> create FY
 * - After success -> redirect to login (ASPX Session.RemoveAll + open AdminLogin.aspx)
 */
const CustomerFinancialYearSetup = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // UI state
  const [showForm, setShowForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Confirm modal
  const [showConfirm, setShowConfirm] = useState(false);

  // Next FY state (from MASTER pending row)
  const [nextFy, setNextFy] = useState({
    finYearId: 0,
    fyCode: "",
    startDate: "",
    endDate: "",
  });

  // Form state
  const [fyrName, setFyrName] = useState("");
  const [errors, setErrors] = useState({});

  // Grid listing
  const [records, setRecords] = useState([]);

  // ------------------ API: load next fy (ASPX fillfinancecode) ------------------
  const fetchNextFy = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/customer-finyear/next`);
      if (!res.data?.status) {
        setNextFy({ finYearId: 0, fyCode: "", startDate: "", endDate: "" });
        toast.error(res.data?.message || "No pending financial year found");
        return;
      }

      setNextFy(res.data.data);
    } catch (err) {
      console.error("Next FY error:", err);
      toast.error("Failed to load next financial year");
    }
  };

  // ------------------ API: listing (ASPX fillgrid) ------------------
  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/customer-finyear/list`);
      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load list");
        setRecords([]);
        return;
      }
      setRecords(res.data.data || []);
    } catch (err) {
      console.error("List error:", err);
      toast.error("Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNextFy();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------ VALIDATION (matches ASPX client JS Validation) ------------------
  const validateForm = () => {
    const e = {};
    if (!String(nextFy.fyCode || "").trim()) e.fyCode = "Please create fin year first";
    if (!String(fyrName || "").trim()) e.fyrName = "Please enter Financial Year Name";
    if (!String(nextFy.startDate || "").trim()) e.startDate = "Please select Financial Year Start Date";
    if (!String(nextFy.endDate || "").trim()) e.endDate = "Please select Financial Year End Date";

    // Validate end date > start date
    const parseDDMMYYYY = (s) => {
      const parts = String(s || "").split("/");
      if (parts.length !== 3) return null;
      const dd = Number(parts[0]);
      const mm = Number(parts[1]);
      const yyyy = Number(parts[2]);
      if (!dd || !mm || !yyyy) return null;
      return new Date(yyyy, mm - 1, dd);
    };

    if (!e.startDate && !e.endDate) {
      const sd = parseDDMMYYYY(nextFy.startDate);
      const ed = parseDDMMYYYY(nextFy.endDate);
      if (sd && ed && ed < sd) {
        e.endDate = "Please select end date greater than start date";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleBack = () => {
  if (showConfirm) setShowConfirm(false);
  navigate(-1);
};

  // ------------------ SAVE CLICK (ASPX btn_save_Click -> shows modal) ------------------
  const handleSaveClick = () => {
    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    // ASPX also checks Session["strFinancialYearStatus"] == "Active"
    // In React, we assume you store it; otherwise remove this check.
    const finStatus = String(localStorage.getItem("strFinancialYearStatus") || "").trim();
    if (finStatus && finStatus !== "Active") {
      toast.error("Please select Active Financial Year and then Create New Financial Year");
      return;
    }

    setShowConfirm(true);
  };

  // ------------------ CONFIRM YES (ASPX btn_yes_Click) ------------------
  const handleConfirmYes = async () => {
    const adminId = Number(localStorage.getItem("userId") || 0); // your template uses userId
    if (!adminId) {
      toast.error("AdminId missing! Please login again.");
      return;
    }

    try {
      setSaving(true);

      const res = await axios.post(
        `${BASE_URL}/api/customer-finyear/create`,
        {
          finYearId: nextFy.finYearId,
          fyrName: fyrName.trim(),
        },
        {
          headers: {
            "x-admin-id": adminId,
          },
        }
      );

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to create financial year");
        setShowConfirm(false);
        return;
      }

      toast.success(res.data?.message || "Financial Year Created Successfully");

      // Refresh list + next
      await fetchList();
      await fetchNextFy();

      // ASPX forces logout and redirects to AdminLogin
      // We'll do same: clear storage + navigate
      const nextAction = res.data?.data?.nextAction;
      if (nextAction === "logout") {
        // clear your auth/localStorage keys as per your app
        localStorage.removeItem("userId");
        localStorage.removeItem("strFinancialYearStatus");
        localStorage.removeItem("finYearId");
        localStorage.removeItem("token");
        toast.info("Please login again (Financial year switched)");
        navigate("/adminlogin"); // adjust to your route
        return;
      }

      setShowConfirm(false);
    } catch (err) {
      console.error("Create FY error:", err);
      toast.error(err.response?.data?.message || "Server error");
      setShowConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmNo = () => {
    setShowConfirm(false);
  };

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CUSTOMER FINANCIAL YEAR SETUP</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
  {/* LEFT SIDE */}
  <div className="flex items-center gap-4">
    <button
      type="button"
      onClick={handleBack}
      disabled={saving}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
        isDark
          ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
          : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
      } disabled:opacity-60`}
    >
      <FaChevronLeft />
      Back
    </button>
    <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
      Financial Year Setup
    </h1>

    <span
      className={`px-3 py-1 rounded-full text-sm font-medium ${
        isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
      }`}
    >
      {records.length} Records
    </span>
  </div>

  {/* RIGHT SIDE */}
  <div className="flex items-center gap-3">
    {/* HELP BUTTON */}
    <button
      onClick={() => window.open("/admin/HelpFile", "_blank")}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow transition ${
        isDark
          ? "bg-blue-700 hover:bg-blue-600 text-white"
          : "bg-blue-600 hover:bg-blue-700 text-white"
      }`}
      title="Help"
    >
      <HelpCircle size={18} />
      <span>Help</span>
    </button>

    {/* TOGGLE FORM BUTTON */}
    <button
      onClick={() => setShowForm(!showForm)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
        isDark
          ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
          : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
      }`}
    >
      {showForm ? <FaChevronUp /> : <FaChevronDown />}
      {showForm ? "Hide Entry Form" : "Show Entry Form"}
    </button>
  </div>
</div>

          {/* Form */}
          <div className={`rounded-xl border mb-6 overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {showForm && (
              <div className={`p-6 border-t ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                <div className="max-w-3xl mx-auto space-y-4">
                  {/* Financial Code */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Financial Code
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={nextFy.fyCode}
                        readOnly
                        className={`w-full px-4 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"
                        } ${errors.fyCode ? "border-red-500" : ""}`}
                      />
                      {errors.fyCode && <p className="text-red-500 text-sm mt-1">{errors.fyCode}</p>}
                    </div>
                  </div>

                  {/* FY Name */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Financial Year Name
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={fyrName}
                        onChange={(e) => {
                          setFyrName(e.target.value);
                          if (errors.fyrName) setErrors((p) => ({ ...p, fyrName: "" }));
                        }}
                        disabled={saving}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                        } ${errors.fyrName ? "border-red-500" : ""}`}
                        placeholder="Enter Financial Year Name..."
                      />
                      {errors.fyrName && <p className="text-red-500 text-sm mt-1">{errors.fyrName}</p>}
                    </div>
                  </div>

                  {/* Start Date */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Financial Year Start Date
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={nextFy.startDate}
                        readOnly
                        className={`w-full px-4 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"
                        } ${errors.startDate ? "border-red-500" : ""}`}
                      />
                      {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
                    </div>
                  </div>

                  {/* End Date */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        Financial Year End Date
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={nextFy.endDate}
                        readOnly
                        className={`w-full px-4 py-2 border rounded-lg ${
                          isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-gray-100 border-gray-300 text-gray-900"
                        } ${errors.endDate ? "border-red-500" : ""}`}
                      />
                      {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
                    </div>
                  </div>

                  {/* Save button */}
                  <div className="flex justify-center gap-4 pt-4">
                    <button
                      onClick={handleSaveClick}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                    >
                      <FaCheck />
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>

                  {/* Message like lblMsg */}
                  <div className="text-center">
                    <span className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm`}>
                      Creating a financial year will switch databases and you will need to login again.
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table (grid) */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Financial Code</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Financial Name</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Start Date</th>
                    <th className="px-4 py-4 text-center font-semibold">End Date</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan="5"
                        className={`px-4 py-6 text-center text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className={`px-4 py-6 text-center text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    records.map((r, idx) => (
                      <tr
                        key={r.fld_cfysid || idx}
                        className={`transition-colors duration-200 ${
                          idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-gray-50") : isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {idx + 1}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_fyrcode}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 font-medium ${isDark ? "text-blue-300" : "text-blue-600"}`}>
                          {r.fld_fyrname}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_fyrstartdate}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_fyrenddate}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Confirm Modal (ASPX ModalPopupExtender) */}
          {showConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/60" onClick={handleConfirmNo} />
              <div
                className={`relative w-[360px] rounded-xl shadow-2xl border p-6 ${
                  isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-200 text-gray-900"
                }`}
              >
                <div className="text-center font-semibold text-lg mb-6">Are You Sure To Create?</div>

                <div className="flex justify-center gap-4">
                  <button
                    onClick={handleConfirmYes}
                    disabled={saving}
                    className={`px-5 py-2 rounded-lg font-semibold ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                  >
                    {saving ? "Creating..." : "Yes"}
                  </button>
                  <button
                    onClick={handleConfirmNo}
                    disabled={saving}
                    className={`px-5 py-2 rounded-lg font-semibold ${
                      isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-900"
                    } disabled:opacity-60`}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default CustomerFinancialYearSetup;
